import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProfileSection = ({ userData, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: userData?.username || '',
    bio: userData?.bio || '',
    voiceDescription: userData?.voiceDescription || '',
    location: userData?.location || '',
    website: userData?.website || ''
  });
  const [avatarPreview, setAvatarPreview] = useState(userData?.avatar);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    onUpdate({
      ...formData,
      avatar: avatarPreview
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      username: userData?.username || '',
      bio: userData?.bio || '',
      voiceDescription: userData?.voiceDescription || '',
      location: userData?.location || '',
      website: userData?.website || ''
    });
    setAvatarPreview(userData?.avatar);
    setIsEditing(false);
  };

  return (
    <div className="profile-section bg-card rounded-lg p-6 shadow-soft">
      <div className="section-header flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="User" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Профиль
            </h2>
            <p className="text-sm text-muted-foreground">
              Управление вашей голосовой идентичностью
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Редактировать
          </Button>
        )}
      </div>
      <div className="profile-content">
        <div className="avatar-section mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Аватар
          </label>
          <div className="flex items-center gap-4">
            <div className="avatar-preview w-24 h-24 rounded-full overflow-hidden bg-muted border-2 border-border">
              <Image
                src={avatarPreview || '/assets/images/no_image.png'}
                alt={`Аватар пользователя ${formData?.username || 'без имени'} с профессиональным фото`}
                className="w-full h-full object-cover"
              />
            </div>
            {isEditing && (
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Upload"
                    iconPosition="left"
                    asChild
                  >
                    <span>Загрузить фото</span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground">
                  JPG, PNG или GIF. Макс. 5MB
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="form-fields space-y-4">
          <Input
            label="Имя пользователя"
            type="text"
            name="username"
            placeholder="Введите имя пользователя"
            value={formData?.username}
            onChange={handleInputChange}
            disabled={!isEditing}
            required
          />

          <div className="bio-field">
            <label className="block text-sm font-medium text-foreground mb-2">
              Биография
            </label>
            <textarea
              name="bio"
              placeholder="Расскажите о себе..."
              value={formData?.bio}
              onChange={handleInputChange}
              disabled={!isEditing}
              rows={4}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {formData?.bio?.length}/500 символов
            </p>
          </div>

          <div className="voice-description-field">
            <label className="block text-sm font-medium text-foreground mb-2">
              Описание голоса
            </label>
            <textarea
              name="voiceDescription"
              placeholder="Опишите свой голос и стиль вещания..."
              value={formData?.voiceDescription}
              onChange={handleInputChange}
              disabled={!isEditing}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed resize-none"
            />
          </div>

          <Input
            label="Местоположение"
            type="text"
            name="location"
            placeholder="Город, страна"
            value={formData?.location}
            onChange={handleInputChange}
            disabled={!isEditing}
          />

          <Input
            label="Веб-сайт"
            type="url"
            name="website"
            placeholder="https://example.com"
            value={formData?.website}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </div>

        {isEditing && (
          <div className="form-actions flex items-center gap-3 mt-6 pt-6 border-t border-border">
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Check"
              iconPosition="left"
            >
              Сохранить изменения
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Отмена
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;